// What the Overview (APS-194, design "Overview") derives from the reads the console already has:
// each app's latest release against the one before, its adoption, the hang or ANR rate that
// fits its platform, and the alert rules that are firing.
import type { AlertRule, Platform, ReleaseHealth, ReleaseVitals } from '../api/types'
import { adoption, previousOf, releasesNewestFirst } from './releases'

export interface AppHealth {
  appId: string
  latest: ReleaseHealth
  previous: ReleaseHealth | undefined
  /** Change in crash-free sessions from the release before, in percentage points. */
  crashFreeDelta: number | undefined
  /** Share of the app's sessions in the last day on its latest release; undefined without any. */
  adoption: number | undefined
  /** Sessions of every release of the app in the window. */
  sessions: number
}

/**
 * Each app's latest release in the window, the release before it and the latest's adoption
 * (from the last day's sessions, as the Releases page measures it). Busiest app first.
 */
export function appsHealth(window: ReleaseHealth[], lastDay: ReleaseHealth[]): AppHealth[] {
  const ids = [...new Set(window.map((r) => r.app_id))]
  return ids
    .map((appId) => {
      const list = releasesNewestFirst(window, appId)
      const latest = list[0] as ReleaseHealth
      const previous = previousOf(list, latest.release)
      const share = adoption(lastDay.filter((r) => r.app_id === appId)).find(
        (a) => a.release === latest.release,
      )?.share
      return {
        appId,
        latest,
        previous,
        crashFreeDelta: previous
          ? (latest.crash_free_sessions_rate - previous.crash_free_sessions_rate) * 100
          : undefined,
        adoption: lastDay.some((r) => r.app_id === appId) ? (share ?? 0) : undefined,
        sessions: list.reduce((n, r) => n + r.sessions, 0),
      }
    })
    .sort((a, b) => b.sessions - a.sessions || a.appId.localeCompare(b.appId))
}

/** Android reports ANRs; every other platform reports hangs (spec 6.3). */
export function freezes(
  platform: Platform | undefined,
  vitals: ReleaseVitals | null | undefined,
): { kind: 'anr' | 'hang'; rate: number | null } | undefined {
  if (!vitals) return undefined
  return platform === 'android'
    ? { kind: 'anr', rate: vitals.hangs.anr_rate }
    : { kind: 'hang', rate: vitals.hangs.hang_rate }
}

/** The project's enabled rules that are firing now, the longest firing first. */
export function firingRules(rules: AlertRule[] | undefined): AlertRule[] {
  return (rules ?? [])
    .filter((r) => r.enabled && r.state === 'firing')
    .sort((a, b) => a.state_since.localeCompare(b.state_since))
}
